import { PlayerRef } from "@remotion/player";
import { useCallback, useEffect, useState } from "react";
import { useTimelineContext } from "../../remotion-timeline/context/use-timeline-context";

type UseVideoPreviewProps = {
  playerRef: React.RefObject<PlayerRef | null>;
  durationInFrames: number;
};

export const useVideoPreview = ({
  playerRef,
  durationInFrames,
}: UseVideoPreviewProps) => {
  const { fps } = useTimelineContext();

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTimeInSeconds, setCurrentTimeInSeconds] = useState(0);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = (e: any) =>
      setCurrentTimeInSeconds(e.detail.frame / fps);

    player.addEventListener("play", onPlay);
    player.addEventListener("pause", onPause);
    player.addEventListener("timeupdate", onTimeUpdate);

    return () => {
      player.removeEventListener("play", onPlay);
      player.removeEventListener("pause", onPause);
      player.removeEventListener("timeupdate", onTimeUpdate);
    };
  }, [playerRef, fps]);

  const togglePlayPause = useCallback(() => {
    if (!playerRef.current) return;
    if (playerRef.current.isPlaying()) {
      playerRef.current.pause();
    } else {
      playerRef.current.play();
    }
  }, [playerRef]);

  const seekBackward = useCallback(() => {
    if (!playerRef.current) return;
    const currentFrame = playerRef.current.getCurrentFrame();
    const newFrame = Math.max(0, currentFrame - fps);
    playerRef.current.seekTo(newFrame);
  }, [playerRef, fps]);

  const seekForward = useCallback(() => {
    if (!playerRef.current) return;
    const currentFrame = playerRef.current.getCurrentFrame();
    // We use the durationInFrames passed in as a prop
    const newFrame = Math.min(durationInFrames - 1, currentFrame + fps);
    playerRef.current.seekTo(newFrame);
  }, [playerRef, fps, durationInFrames]);

  const toggleFullscreen = useCallback(() => {
    if (!playerRef.current) {
      console.warn("Video container element not found");
      return;
    }
    try {
      if (!document.fullscreenElement) {
        (playerRef.current as any).requestFullscreen();
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      }
    } catch (error) {
      console.error("Fullscreen API error:", error);
    }
  }, [playerRef]);

  const getTotalDurationInSeconds = useCallback(() => {
    return durationInFrames / fps;
  }, [durationInFrames, fps]);

  return {
    isPlaying,
    currentTimeInSeconds,
    totalDurationInSeconds: getTotalDurationInSeconds(),
    togglePlayPause,
    seekForward,
    seekBackward,
    toggleFullscreen,
  };
};
